<script lang="ts">
import { RatingTarget } from '@smartsupp/smartsupp-message/lib/enums'
import { onMount } from 'svelte'
import { slide } from 'svelte/transition'

import Button from '@/components/core/Button.svelte'
import Textarea from '@/components/core/Textarea.svelte'
import Drawer from '@/components/drawer/Drawer.svelte'
import DrawerContent from '@/components/drawer/DrawerContent.svelte'
import DrawerHeader from '@/components/drawer/DrawerHeader.svelte'
import { testIds } from '@/constants'
import { forceContentListScroll } from '@/modules/app'
import {
	type ChatRatingInfo,
	chatRatingInfoMap,
	getChatRatingImageUrl,
	rateChat,
	ratingInfoArray,
	ratingMessageId,
} from '@/modules/chatRating'
import { isTouchDevice } from '@/modules/device'
import { closeDrawer } from '@/modules/drawers'
import { successMessage } from '@/modules/flashMessages'
import { RATING_TEXT_MAXLENGTH } from '@/modules/form'
import { chatRating } from '@/modules/messages'
import { getFromStorage, removeFromStorage, setToStorage, StorageItem } from '@/modules/storage'
import { t } from '@/modules/translations'

import ChatRatingEmojiButton from './ChatRatingEmojiButton.svelte'

let text = $state('')

onMount(() => {
	const ratingText = getFromStorage(StorageItem.RatingText)
	if (ratingText) {
		text = ratingText
	}
})

const handleRatingClick = (value: number) => async () => {
	if (!$chatRating) return
	await rateChat({
		messageId: $chatRating.messageId,
		rating: { value, text },
		onSuccess: () => {
			forceContentListScroll()
			successMessage($t('rating.submit.success'))
		},
	})
}

const sendTextRating = async () => {
	if (!$chatRating?.messageId || !$chatRating.value || text.length === 0) return
	await rateChat({
		messageId: $chatRating.messageId,
		rating: { value: $chatRating.value, text },
		onSuccess: () => {
			removeFromStorage(StorageItem.RatingText)
			forceContentListScroll()
			successMessage($t('rating.submitFeedback.success'))
			handleCloseDrawer()
		},
	})
}

const handleCloseDrawer = () => {
	ratingMessageId.set(null)
	closeDrawer()
}

const handleFeedbackInput = (e: Event) => {
	const target = e.target as HTMLTextAreaElement
	setToStorage({ name: StorageItem.RatingText, value: target.value })
}

let ratingValue = $derived($chatRating?.value)
let hasRatingValue = $derived(!!ratingValue)
let ratingInfo: ChatRatingInfo | undefined = $derived(chatRatingInfoMap[ratingValue ?? 0])
let isTextareaEmpty = $derived(text.length === 0)

const { chatRatingDrawer, chatRatingTextarea, chatRatingSendButton } = testIds
</script>

<Drawer onClose={handleCloseDrawer} testId={chatRatingDrawer}>
	<DrawerHeader
		title={$chatRating?.target === RatingTarget.Ai ? $t('rating.wasItHelpful.ai') : $t('rating.wasItHelpful.agent')}
	/>
	<DrawerContent>
		{#if $chatRating}
			<div class="flex-center mt-2 mb-4">
				{#each ratingInfoArray as r (r.value)}
					<ChatRatingEmojiButton
						isDisabled={hasRatingValue}
						isFaded={hasRatingValue && ratingValue !== r.value}
						onClick={handleRatingClick(r.value)}
					>
						<img src={getChatRatingImageUrl(r.name)} alt={r.name} />
					</ChatRatingEmojiButton>
				{/each}
			</div>
			{#if hasRatingValue}
				{#if ratingInfo}
					<div transition:slide class="flex-center mb-4 text-sm">
						{$t(ratingInfo.text)}
					</div>
				{/if}
				<div transition:slide class="mb-4">
					<div class="mb-6">
						<Textarea
							bind:value={text}
							rows={3}
							placeholder={$t('agentRating.all.placeholder')}
							maxlength={RATING_TEXT_MAXLENGTH}
							onInput={handleFeedbackInput}
							testId={chatRatingTextarea}
							{...!isTouchDevice() && { autofocus: true }}
						/>
					</div>
					<Button disabled={isTextareaEmpty} isBlock onClick={sendTextRating} testId={chatRatingSendButton}
						>{$t('general.send')}</Button
					>
				</div>
			{/if}
		{:else}
			<div class="text-sm text-red-500">Chat rating not found</div>
		{/if}
	</DrawerContent>
</Drawer>
